import { useState, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseModal } from '../ui/BaseModal';
import { useGetAllRecipients, useResendDocument } from '../../services/documents.service';
import { RecipientRow } from './RecipientRow';

interface ResendDocumentModalProps {
  companyId: string;
}

export interface ResendDocumentModalRef {
  open: (documentId: string) => void;
}

export const ResendDocumentModal = forwardRef<ResendDocumentModalRef, ResendDocumentModalProps>(
  ({ companyId }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [documentId, setDocumentId] = useState<string | null>(null);
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

    const { data: recipientsData, isLoading } = useGetAllRecipients(companyId, documentId ?? '');
    const resendMutation = useResendDocument(companyId);

    const recipients = recipientsData?.recipients ?? [];
    const documentName = recipientsData?.documentName ?? '';

    useImperativeHandle(ref, () => ({
      open: (docId: string) => {
        setDocumentId(docId);
        setSelectedUserIds(new Set());
        setIsOpen(true);
      },
    }));

    useEffect(() => {
      if (recipients.length > 0 && selectedUserIds.size === 0) {
        const notOpenedIds = recipients.filter((r) => !r.hasOpened).map((r) => r.userCompanyId);
        setSelectedUserIds(new Set(notOpenedIds));
      }
    }, [recipients, selectedUserIds.size]);

    useEffect(() => {
      if (!isOpen) {
        setSelectedUserIds(new Set());
        setDocumentId(null);
      }
    }, [isOpen]);

    const handleToggleUser = (userCompanyId: string) => {
      setSelectedUserIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(userCompanyId)) {
          newSet.delete(userCompanyId);
        } else {
          newSet.add(userCompanyId);
        }
        return newSet;
      });
    };

    const handleToggleAll = () => {
      if (selectedUserIds.size === recipients.length) {
        setSelectedUserIds(new Set());
      } else {
        setSelectedUserIds(new Set(recipients.map((r) => r.userCompanyId)));
      }
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!documentId || selectedUserIds.size === 0) return;

      resendMutation.mutate(
        { documentId, userCompanyIds: Array.from(selectedUserIds) },
        { onSuccess: handleClose },
      );
    };

    const handleClose = () => {
      setIsOpen(false);
    };

    const isSubmitting = resendMutation.isPending;
    const canSubmit = selectedUserIds.size > 0 && !isSubmitting;

    const sortedRecipients = useMemo(() => {
      return [...recipients].sort((a, b) => {
        if (a.hasOpened !== b.hasOpened) {
          return a.hasOpened ? 1 : -1;
        }
        return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
      });
    }, [recipients]);

    const footer = (
      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
          disabled={isSubmitting}
        >
          {t('documents.resend_modal.cancel')}
        </button>
        <button
          type="submit"
          form="resend-document-form"
          className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition disabled:opacity-50"
          disabled={!canSubmit}
        >
          {t('documents.resend_modal.submit')}
        </button>
      </div>
    );

    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('documents.resend_modal.title')}
        footer={footer}
        maxWidth="2xl"
      >
        <form
          id="resend-document-form"
          onSubmit={handleSubmit}
          className="flex flex-1 min-h-0 flex-col gap-6"
        >
          <div className="flex-shrink-0">
            <p className="text-base font-medium text-gray-900 mb-2">
              {t('documents.resend_modal.document_label')}
            </p>
            <div className="border border-gray-200 rounded-md px-3 py-2.5">
              <span className="text-sm font-medium text-gray-900">{documentName}</span>
            </div>
          </div>

          <div className="flex flex-1 min-h-0 flex-col gap-3">
            <p className="flex-shrink-0 text-base font-medium text-gray-900">
              {t('documents.resend_modal.recipients_label')}
            </p>

            {isLoading ? (
              <div className="text-center py-8 text-gray-500">{t('common.loading')}</div>
            ) : recipients.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('documents.resend_modal.no_recipients')}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-md flex flex-1 min-h-0 flex-col overflow-hidden">
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-12 px-3 py-2.5 text-left">
                          <input
                            type="checkbox"
                            checked={
                              selectedUserIds.size === recipients.length && recipients.length > 0
                            }
                            onChange={handleToggleAll}
                            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </th>
                        <th className="px-3 py-2.5 text-left text-sm font-medium text-gray-500">
                          {t('documents.resend_modal.columns.user')}
                        </th>
                        <th className="px-3 py-2.5 text-left text-sm font-medium text-gray-500">
                          {t('documents.resend_modal.columns.status')}
                        </th>
                        <th className="px-3 py-2.5 text-left text-sm font-medium text-gray-500">
                          {t('documents.resend_modal.columns.group')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRecipients.map((recipient) => (
                        <RecipientRow
                          key={recipient.userCompanyId}
                          recipient={recipient}
                          isSelected={selectedUserIds.has(recipient.userCompanyId)}
                          onToggle={() => handleToggleUser(recipient.userCompanyId)}
                          t={t}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex-shrink-0 px-3 py-2 text-xs text-gray-600 border-t border-gray-200">
                  {t('documents.resend_modal.selection', {
                    selected: selectedUserIds.size,
                    total: recipients.length,
                  })}
                </div>
              </div>
            )}
          </div>
        </form>
      </BaseModal>
    );
  },
);

ResendDocumentModal.displayName = 'ResendDocumentModal';
